import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import axios from 'axios'
import { API_BASE_URL } from '../../utils/api'
import LoadingSpinner from '../LoadingSpinner'

interface Permission {
  key: string
  name: string
  description: string
  group: string
}

interface TeamMember {
  id: number
  first_name: string
  last_name: string
  email: string
  role: string
  department_name?: string
  role_template_name?: string
  permissions: { [key: string]: boolean }
}

interface PermissionGroup {
  name: string
  color: string
  permissions: string[]
}

const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    name: 'Data Access',
    color: 'bg-blue-100 text-blue-800',
    permissions: ['viewCustomers', 'viewDeals', 'viewTasks', 'viewReports', 'viewFinancials', 'viewCallLogs']
  },
  {
    name: 'Data Management',
    color: 'bg-green-100 text-green-800',
    permissions: ['editTasks', 'editDeals', 'editCustomers', 'managePipeline', 'manageProperties']
  },
  {
    name: 'Communication',
    color: 'bg-yellow-100 text-yellow-800',
    permissions: ['manageSms', 'manageIntegrations']
  },
  {
    name: 'Team Management',
    color: 'bg-purple-100 text-purple-800',
    permissions: ['manageTeam', 'viewTeamAnalytics', 'manageRoles', 'viewAuditLogs']
  },
  {
    name: 'System Administration',
    color: 'bg-red-100 text-red-800',
    permissions: ['manageSettings', 'manageWorkflows', 'exportData']
  }
]

const PERMISSION_LABELS: { [key: string]: { name: string; description: string } } = {
  // Legacy permissions
  viewOnly: { name: 'View Only', description: 'Limited to viewing data only' },
  editTasks: { name: 'Edit Tasks', description: 'Create and modify tasks' },
  editDeals: { name: 'Edit Deals', description: 'Create and modify deals' },
  editCustomers: { name: 'Edit Customers', description: 'Create and modify customers' },
  manageSms: { name: 'Manage SMS', description: 'Send and manage SMS messages' },
  
  // Extended permissions
  viewCustomers: { name: 'View Customers', description: 'Access customer information' },
  viewDeals: { name: 'View Deals', description: 'Access deal information' },
  viewTasks: { name: 'View Tasks', description: 'Access task information' },
  viewReports: { name: 'View Reports', description: 'Access analytics and reports' },
  viewFinancials: { name: 'View Financials', description: 'Access commission and financial data' },
  viewCallLogs: { name: 'View Call Logs', description: 'Access call history and recordings' },
  exportData: { name: 'Export Data', description: 'Download and export data' },
  manageTeam: { name: 'Manage Team', description: 'Invite and manage team members' },
  manageSettings: { name: 'Manage Settings', description: 'Modify workspace settings' },
  managePipeline: { name: 'Manage Pipeline', description: 'Edit deal stages and statuses' },
  manageProperties: { name: 'Manage Properties', description: 'Property management access' },
  manageWorkflows: { name: 'Manage Workflows', description: 'Create and edit workflows' },
  manageIntegrations: { name: 'Manage Integrations', description: 'Configure SMS and call settings' },
  manageRoles: { name: 'Manage Roles', description: 'Create and edit role templates' },
  viewAuditLogs: { name: 'View Audit Logs', description: 'Access activity and audit logs' },
  viewTeamAnalytics: { name: 'View Team Analytics', description: 'Access team performance data' }
}

export const PermissionMatrix: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null)
  const [saving, setSaving] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<string>('Data Access')

  useEffect(() => {
    fetchTeamMembers()
  }, [])

  const fetchTeamMembers = async () => {
    try {
      setLoading(true)
      
      console.log('🔍 DEBUG: Fetching collaborators directly')
      console.log('- API URL:', `${API_BASE_URL}/api/collaborators`)
      
      // Get collaborators directly from the collaborators endpoint
      const response = await axios.get(`${API_BASE_URL}/api/collaborators`)
      
      console.log('✅ DEBUG: Collaborators response received')
      console.log('- Response status:', response.status)
      console.log('- Raw response data:', response.data)
      
      const members = response.data.collaborators.map((collaborator: any) => ({
        id: collaborator.id, // This is the collaborator ID, not user ID
        user_id: collaborator.user_id, // Store user ID separately if needed
        first_name: collaborator.first_name,
        last_name: collaborator.last_name,
        email: collaborator.email,
        role: 'collaborator',
        department_name: collaborator.department_name,
        permissions: collaborator.permissions || {}
      }))
      
      console.log('🔄 DEBUG: Processed collaborator members:', members)
      console.log('- Total collaborators found:', members.length)
      members.forEach((member: any, index: number) => {
        console.log(`- Collaborator ${index + 1}:`, {
          id: member.id,
          user_id: member.user_id,
          name: `${member.first_name} ${member.last_name}`,
          permissions: member.permissions
        })
      })
      
      setTeamMembers(members)
      if (members.length > 0) {
        setSelectedMember(members[0])
        console.log('✅ DEBUG: Selected first collaborator:', {
          id: members[0].id,
          user_id: members[0].user_id,
          name: `${members[0].first_name} ${members[0].last_name}`
        })
      }
    } catch (error: any) {
      console.error('❌ DEBUG: Error fetching collaborators')
      console.log('- Error type:', typeof error)
      console.log('- Error message:', error.message)
      console.log('- Error response status:', error.response?.status)
      console.log('- Error response data:', error.response?.data)
      console.log('- Full error object:', error)
    } finally {
      setLoading(false)
    }
  }

  const updatePermission = async (userId: number, permission: string, value: boolean) => {
    try {
      setSaving(true)
      
      console.log('🔍 DEBUG: Starting permission update')
      console.log('- User ID:', userId)
      console.log('- Permission:', permission)
      console.log('- Value:', value)
      console.log('- API Base URL:', API_BASE_URL)
      
      // Get current member's permissions
      const currentMember = teamMembers.find(member => member.id === userId)
      if (!currentMember) {
        console.error('❌ DEBUG: Current member not found for userId:', userId)
        console.log('Available team members:', teamMembers.map(m => ({ id: m.id, name: `${m.first_name} ${m.last_name}` })))
        return
      }
      
      console.log('✅ DEBUG: Current member found:', {
        id: currentMember.id,
        name: `${currentMember.first_name} ${currentMember.last_name}`,
        currentPermissions: currentMember.permissions
      })
      
      // Create updated permissions object
      const updatedPermissions = {
        ...currentMember.permissions,
        [permission]: value
      }
      
      console.log('🔄 DEBUG: Updated permissions object:', updatedPermissions)
      
      const apiUrl = `${API_BASE_URL}/api/collaborators/${userId}/permissions`
      const requestBody = { permissions: updatedPermissions }
      
      console.log('📡 DEBUG: Making API request')
      console.log('- URL:', apiUrl)
      console.log('- Method: PUT')
      console.log('- Body:', requestBody)
      console.log('- Headers:', {
        'Authorization': localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : 'No token',
        'Content-Type': 'application/json'
      })
      
      const response = await axios.put(apiUrl, requestBody)
      
      console.log('✅ DEBUG: API request successful')
      console.log('- Response status:', response.status)
      console.log('- Response data:', response.data)
      
      // Update local state
      setTeamMembers(prev => prev.map(member => 
        member.id === userId 
          ? { ...member, permissions: { ...member.permissions, [permission]: value } }
          : member
      ))
      
      // Update selected member if it's the one being updated
      if (selectedMember && selectedMember.id === userId) {
        setSelectedMember(prev => prev ? { ...prev, permissions: { ...prev.permissions, [permission]: value } } : null)
      }
      
      console.log('✅ DEBUG: Local state updated successfully')
      
    } catch (error: any) {
      console.error('❌ DEBUG: Error updating permission')
      console.log('- Error type:', typeof error)
      console.log('- Error message:', error.message)
      console.log('- Error response status:', error.response?.status)
      console.log('- Error response data:', error.response?.data)
      console.log('- Error config URL:', error.config?.url)
      console.log('- Error config method:', error.config?.method)
      console.log('- Error config data:', error.config?.data)
      console.log('- Full error object:', error)
    } finally {
      setSaving(false)
    }
  }

  const getCurrentGroup = () => {
    return PERMISSION_GROUPS.find(group => group.name === selectedGroup) || PERMISSION_GROUPS[0]
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (teamMembers.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Team Members</h3>
        <p className="text-gray-500">Add collaborators to manage their permissions.</p>
      </div>
    )
  }

  const currentGroup = getCurrentGroup()

  return (
    <div className="space-y-6">
      {/* Clean Two-Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Panel: Team Member List */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Team Members</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Select a member to manage permissions</p>
            </div>
            <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
              {teamMembers.map((member) => (
                <button
                  key={member.id}
                  onClick={() => setSelectedMember(member)}
                  className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedMember?.id === member.id 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-gray-900' 
                      : ''
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                      <span className="text-gray-900 dark:text-gray-600 font-medium text-sm">
                        {member.first_name[0]}{member.last_name[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {member.first_name} {member.last_name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {member.email}
                      </div>
                      {member.department_name && (
                        <div className="text-xs text-gray-900 dark:text-gray-600 mt-1">
                          📁 {member.department_name}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel: Permission Management */}
        <div className="lg:col-span-2">
          {selectedMember && (
            <div className="space-y-6">
              
              {/* Selected Member Header */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-gray-900 dark:text-gray-600 font-medium text-lg">
                      {selectedMember.first_name[0]}{selectedMember.last_name[0]}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedMember.first_name} {selectedMember.last_name}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400">{selectedMember.email}</p>
                    <div className="flex items-center space-x-3 mt-2">
                      {selectedMember.department_name && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                          📁 {selectedMember.department_name}
                        </span>
                      )}
                      {selectedMember.role_template_name && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          🎭 {selectedMember.role_template_name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Permission Group Tabs */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="flex space-x-1 p-1">
                    {PERMISSION_GROUPS.map((group) => (
                      <button
                        key={group.name}
                        onClick={() => setSelectedGroup(group.name)}
                        className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                          selectedGroup === group.name
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        {group.name}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Permission List for Selected Group */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {currentGroup.name} Permissions
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Manage access for {selectedMember.first_name} in this category
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${currentGroup.color}`}>
                      {currentGroup.permissions.filter(p => selectedMember.permissions[p]).length} / {currentGroup.permissions.length} enabled
                    </span>
                  </div>

                  <div className="space-y-4">
                    {currentGroup.permissions.map((permission) => {
                      const permissionInfo = PERMISSION_LABELS[permission]
                      const isEnabled = selectedMember.permissions[permission] || false
                      
                      return (
                        <div key={permission} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <div className={`w-2 h-2 rounded-full ${isEnabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                              <div>
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                  {permissionInfo?.name || permission}
                                </h4>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {permissionInfo?.description || 'No description available'}
                                </p>
                              </div>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={isEnabled}
                              onChange={(e) => updatePermission(selectedMember.id, permission, e.target.checked)}
                              disabled={saving}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-gray-900"></div>
                          </label>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {saving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex items-center space-x-3">
            <LoadingSpinner />
            <span className="text-gray-600 dark:text-gray-300">Updating permissions...</span>
          </div>
        </div>
      )}
    </div>
  )
}
